import { Box, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../context/GlobalContext";
import AccountIcon from "../assets/accounts/AccountsIcon.svg";
import RightArrowIcon from "../assets/common/right-arrow-icon.svg";
import "../styles/interactions.css";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";
import AppNotification from "../components/Common/AppNotification";
import TaskNotifications from "../components/ActionItems/TaskNotifications";
import useGlobalContext from "../context/useGlobalContext";
import { useNotificationCount } from "../hooks/useNotificationCount";
import Accounts, { CustomerSavedViewsState } from "../components/Accounts/Accounts";
import ClientAsk7G from "../components/ClientOverview/ClientAsk7G";
import SaveViewPreferences from "../components/Analytics/SaveViewPreferences";

interface DateRangeValue {
  preset: string;
  startDate: string;
  endDate: string;
}

const AccountPage = () => {
  const { accountDetails } = useContext(GlobalContext);
  const [accountDateRange, setAccountDateRange] = useState<DateRangeValue>({ preset: "currentWeek", startDate: "", endDate: "" });
  const location = useLocation();
  const pathParts = location.pathname.split("/"); 
  const lastPart = pathParts[pathParts.length - 1];
  const { featureMap, impersonationDetails } = useGlobalContext();
  const isNotificationsEnabled = featureMap?.in_app_notifications === true;

  const accountLabels: Record<string, string> = {
    prospect: "Prospects",
    customer: "Customers",
    vendor: "Vendors",
    spaces: "Spaces",
  };

  const userProfileOrgId = accountDetails?.profileOrgId ?? "";
  const userNotification = useNotificationCount(userProfileOrgId, isNotificationsEnabled);
  const Ask7GEnabled = featureMap?.business_ask7g === true;
  const [isAsk7GExpanded, setIsAsk7GExpanded] = useState(false);
  const [customerSavedViewsState, setCustomerSavedViewsState] = useState<CustomerSavedViewsState | null>(null);
  const [isSpaceMode, setIsSpaceMode] = useState(false);
  const isImpersonating = impersonationDetails?.isImpersonating === true;

  useEffect(() => {
    setIsAsk7GExpanded(false);
  }, [location.pathname, location.search]);

  return (
    <Box
      id="meetingsParent"
      className="flex flex-col justify-start items-start gap-6 m-2"
      style={{ width: "auto" }}
    >
      <div className="interaction-breadcrumbs flex flex-row items-center w-full mt-2">
      <div className="flex flex-row justify-start items-center gap-2">
        <Box
          component={isAsk7GExpanded ? "button" : "div"}
          type={isAsk7GExpanded ? "button" : undefined}
          className={`flex flex-row justify-start items-center gap-2 ask7g-home-breadcrumb ${
            isAsk7GExpanded ? "ask7g-home-breadcrumb--expanded" : "ask7g-home-breadcrumb--collapsed"
          }`}
          onClick={isAsk7GExpanded ? () => setIsAsk7GExpanded(false) : undefined}
        >
        <img src={AccountIcon} alt="" className="breadCrumbs-icon"/> <h2>Account</h2>
        </Box>
      {accountLabels[lastPart] && !isAsk7GExpanded && (
        <>
          <img src={RightArrowIcon} alt="" style={{marginTop: "3px"}}/>
          <h1 className="interactions-title">
            {accountLabels[lastPart]}
            {accountDateRange?.startDate && accountDateRange?.endDate && (
              <span className="interactions-date-range"> ({format(new Date(accountDateRange?.startDate), "MMM dd yyyy")} - {format(new Date(accountDateRange?.endDate), "MMM dd yyyy")})</span>
            )}
          </h1>
        </>
      )}
      {Ask7GEnabled && isAsk7GExpanded && (
        <>
          <img src={RightArrowIcon} alt="" style={{marginTop: "3px"}} />
          <Typography className="breadCrumbs-text ask7g-current-breadcrumb" sx={{fontSize: "16px"}}>
            Ask 7G
          </Typography>
        </>
      )}
      </div>
      <div className="ml-auto flex flex-row items-center gap-3">
        {lastPart === "customer" && customerSavedViewsState && !isImpersonating && !isAsk7GExpanded &&(
          <SaveViewPreferences
            salesforceReportPref={customerSavedViewsState.salesforceReportPref}
            preferenceKey="customer_preferences"
            activeFilterCount={customerSavedViewsState.activeFilterCount}
            visibleColumnCount={customerSavedViewsState.visibleColumnCount}
            hasUnsavedChanges={customerSavedViewsState.hasUnsavedChanges}
            activeViewName={customerSavedViewsState.activeViewName}
            currentConfigSnapshot={customerSavedViewsState.currentConfigSnapshot}
            onSelectView={customerSavedViewsState.onSelectView}
            onClearAllFilters={customerSavedViewsState.onClearAllFilters}
            getFilterCount={customerSavedViewsState.getFilterCount}
            getColumnCount={customerSavedViewsState.getColumnCount}
          />
        )}
        {isNotificationsEnabled && (
          <AppNotification
          notificationCount= {userNotification.notificationCount}
          notificationApiRefetch = {userNotification.getNotificationCountRefetch}
          contentComponent={<TaskNotifications userNotification={userNotification}/>}
          />
        )}
        {Ask7GEnabled && (
          <ClientAsk7G
            view="icon"
            isExpanded={isAsk7GExpanded}
            onToggleExpand={setIsAsk7GExpanded}
            isSpaceMode={isSpaceMode}
          />
        )}
        </div>
      </div>
      {Ask7GEnabled && (
        <Box sx={{ width: "100%", display: isAsk7GExpanded ? "block" : "none" }}>
          <ClientAsk7G
            view="full"
            isExpanded={isAsk7GExpanded}
            onToggleExpand={setIsAsk7GExpanded}
            isSpaceMode={isSpaceMode}
          />
        </Box>
      )}
      <Box sx={{ display: isAsk7GExpanded ? "none" : "block", width: "100%" }}>
        <Accounts
          setAccountDateRange={setAccountDateRange}
          onCustomerSavedViewsChange={setCustomerSavedViewsState}
          onSpaceModeChange={setIsSpaceMode}
        />
      </Box>
    </Box>
  );
};

export default AccountPage;
